import React, { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from 'src/store';
import DataTable from 'src/shared/components/data-table/DataTable';
import { DataTableColumns, DataTableState, SortingDirection, TableRowData } from 'src/shared/components/data-table/dataTableTypes';
import classNames from 'classnames';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import Loader from 'src/shared/components/loader/Loader';
import Show from 'src/shared/components/show/Show';
import { HabitatInsightsMetricType, habitatInsightsMetricTypeLabel } from 'src/shared/types';
import { getCurrentPortfolioFilters } from 'src/app/portfolio/state/portfolioGeneralSelector';
import {
    getPortfolioSiteExplorerState,
    getSelectedSiteId,
} from 'src/app/portfolio/views/portfolio-site-explorer/state/portfolioSiteExplorerSelector';
import Select, { SelectOption } from 'src/shared/components/select/Select';
import { InputLabel } from 'src/shared/components/ui/input-label';
import { useAppDispatch } from 'src/store';
import { setSelectedMetric, setSelectedSite } from 'src/app/portfolio/views/portfolio-site-explorer/state/portfolioSiteExplorerSlice';
import { getPortfolioMetricColor } from 'src/app/portfolio/helpers/portfolioHelpers';
import QuestionButtonHelp from 'src/shared/components/question-button-help/QuestionButtonHelp';
import { habitatInsightsMetricExplainerSlugs } from 'src/shared/constants/habitatInsights';
import { ArrowLeft as ArrowBackIcon, X as CloseIcon } from 'lucide-react';
import { useIsGridBackedSpecies } from 'src/app/portfolio/state/portfolioSpeciesSource';
import styles from './PortfolioSiteExplorerMapSidebar.module.scss';
import { usePortfolioMapFiltersQuery } from '../../state/api/portfolioSiteExplorerGraphSlice';
import usePortfolioSiteTable from '../../hooks/usePortfolioSiteTable';
import ViewButton from '../view-button/ViewButton';
import SiteSpeciesRiskModal from '../site-species-risk-modal/SiteSpeciesRiskModal';
import usePortfolioSiteExplorerAnalytics from '../../hooks/usePortfolioSiteExplorerAnalytics';

const getMetricValueColumnHeader = (metric: string) => {
    if (metric === 'POSSIBLE_IUCN_RED_LIST_SPECIES') {
        return 'Predicted IUCN Red List species';
    }
    if (metric === 'POSSIBLE_INVASIVE_SPECIES') {
        return 'Predicted invasive species';
    }
    if (metric === HabitatInsightsMetricType.LAND_COVER) {
        return 'Combined vegetation cover'; // as set in metric options
    }
    return habitatInsightsMetricTypeLabel[metric as HabitatInsightsMetricType] || metric;
};

const defaultSorting = {
    columnId: 'metric',
    sortedDirection: SortingDirection.ASC,
};

/** Maps DataTable columnId to the backend sort field name. The metric column maps dynamically based on the selected metric. */
const getBackendSortField = (columnId: string, selectedMetric: string): string | null => {
    if (columnId === 'name') return 'name';
    if (columnId === 'metric') return selectedMetric !== 'NA' ? selectedMetric : null;
    return null;
};

const toApiSortOrder = (dir: SortingDirection): 'ASC' | 'DESC' => (dir === SortingDirection.DESC ? 'DESC' : 'ASC');

const PortfolioSiteExplorerSidebar = ({
    isSidebarOpen,
    setSidebarOpen,
}: {
    isSidebarOpen: boolean;
    setSidebarOpen: (open: boolean) => void;
}) => {
    const dispatch = useAppDispatch();
    const selectedFilters = useAppSelector(getCurrentPortfolioFilters);
    const uiState = useAppSelector(getPortfolioSiteExplorerState);
    const { selectedMetric } = uiState;
    const selectedSiteId = useAppSelector(getSelectedSiteId);
    const { currentCustomerId } = useCustomer();
    const {
        trackDetailedExplainerOpen,
        trackSpeciesRiskModalOpen,
        trackSidebarIndicatorHelpHover,
        trackSidebarIndicatorSelect,
        trackSiteClick,
    } = usePortfolioSiteExplorerAnalytics();
    const [dataTableState, setDataTableState] = useState<Partial<DataTableState>>({
        data: [],
        hiddenColumnIds: new Set(['siteId']),
        selectedRowIds: new Set([selectedSiteId || '']),
        sortedColumn: defaultSorting,
        rowsPerPage: 20,
        currentPageNumber: 1,
    });
    const [isSpeciesModalOpen, setSpeciesModalOpen] = useState(false);
    const [speciesModalTab, setSpeciesModalTab] = useState<'iucn' | 'invasives'>('iucn');
    const [speciesModalSiteId, setSpeciesModalSiteId] = useState<string | null>(null);
    const isSpeciesMetric = selectedMetric === 'POSSIBLE_IUCN_RED_LIST_SPECIES' || selectedMetric === 'POSSIBLE_INVASIVE_SPECIES';
    const isGridBacked = useIsGridBackedSpecies();

    // Only store user-driven sort interactions. The default field is always derived
    // from the current metric so it's never stale after a metric change.
    const [sortOverride, setSortOverride] = useState<{ field: string; order: 'ASC' | 'DESC' } | null>(null);
    // The server sort fields for the two species columns. Named after the row fields
    // rather than after a metric, because the counts are not in the metrics array.
    const speciesSortField = selectedMetric === 'POSSIBLE_IUCN_RED_LIST_SPECIES' ? 'speciesRedListCount' : 'speciesInvasiveCount';
    const defaultSortField = isSpeciesMetric
        ? // The projection cannot order by these at all, so it falls back to name.
          isGridBacked
            ? speciesSortField
            : 'name'
        : selectedMetric !== 'NA'
        ? selectedMetric
        : 'name';
    const sortState = sortOverride ?? { field: defaultSortField, order: 'ASC' as const };

    const pageSize = dataTableState.rowsPerPage ?? 20;

    const {
        rows,
        totalCount,
        isFetching: isSiteMetricsFetching,
        isServerSideSort,
        canSortSpeciesColumns,
        goToPage,
    } = usePortfolioSiteTable({
        customerId: currentCustomerId,
        ...(!isSpeciesMetric ? { metric: selectedMetric !== 'NA' ? selectedMetric : 'Tree_Connectivity' } : {}),
        ...(selectedFilters.length ? { filters: selectedFilters } : {}),
        pageSize,
        ...(!isSpeciesMetric || isGridBacked ? { sortBy: sortState } : {}),
        skip: selectedMetric === 'NA',
    });

    // Reset page and clear any user sort override when metric or filters change
    useEffect(() => {
        goToPage(1);
        setSortOverride(null);
        setDataTableState(prev => ({
            ...prev,
            currentPageNumber: 1,
            sortedColumn: isSpeciesMetric ? undefined : defaultSorting,
        }));
    }, [selectedMetric, selectedFilters]);

    // Once client-side mode is confirmed, species columns become sortable — apply the default highlight.
    useEffect(() => {
        if (!isSpeciesMetric || !canSortSpeciesColumns) return;
        setDataTableState(prev => ({ ...prev, sortedColumn: defaultSorting }));
    }, [selectedMetric, canSortSpeciesColumns]);

    // Build color domain from current page rows for species metrics
    const speciesColorDomain: { min: number; max: number } | undefined = useMemo(() => {
        if (!isSpeciesMetric) return undefined;
        const values = rows.map(r =>
            selectedMetric === 'POSSIBLE_IUCN_RED_LIST_SPECIES' ? r.speciesRedListCount ?? 0 : r.speciesInvasiveCount ?? 0
        );
        if (values.length === 0) return undefined;
        const max = Math.max(...values);
        if (!(max > 0)) return undefined;
        return { min: Math.min(...values), max };
    }, [rows, selectedMetric, isSpeciesMetric]);

    // DataTable columns
    const columns: DataTableColumns = [
        {
            columnId: 'siteId',
            title: 'Site ID',
            isUnique: true,
        },
        {
            columnId: 'metric',
            title: getMetricValueColumnHeader(selectedMetric),
            isSortable: !isSpeciesMetric || canSortSpeciesColumns,
            width: '120px',
            renderer: ({ rowData }) => {
                const val = rowData[1] as number;
                const color = getPortfolioMetricColor(val, selectedMetric, speciesColorDomain);
                const content = (
                    <div className='flex flex-row gap-2 items-center'>
                        <div style={{ width: 12, height: 12, background: color, borderRadius: '100%', border: '1px white solid' }} />
                        {val}
                    </div>
                );
                if (isSpeciesMetric) {
                    const siteId = rowData[0] as string;
                    const tab: 'iucn' | 'invasives' = selectedMetric === 'POSSIBLE_IUCN_RED_LIST_SPECIES' ? 'iucn' : 'invasives';
                    return (
                        <button
                            type='button'
                            className='text-primary underline'
                            onClick={e => {
                                e.stopPropagation();
                                setSpeciesModalSiteId(siteId);
                                setSpeciesModalTab(tab);
                                setSpeciesModalOpen(true);
                                trackSpeciesRiskModalOpen(siteId, tab === 'invasives' ? 'invasive' : tab, 'sidebar');
                            }}
                        >
                            {content}
                        </button>
                    );
                }
                return content;
            },
        },
        {
            columnId: 'name',
            title: 'Site Name',
            isSortable: !isSpeciesMetric || canSortSpeciesColumns,
            width: '200px',
        },
        {
            columnId: 'siteLevelData',
            title: 'Site level data',
            isSortable: false,
            width: '80px',
        },
    ];

    useEffect(() => {
        const tableRows = rows.map(row => {
            const metricValue =
                selectedMetric === 'POSSIBLE_IUCN_RED_LIST_SPECIES'
                    ? row.speciesRedListCount ?? 0
                    : selectedMetric === 'POSSIBLE_INVASIVE_SPECIES'
                    ? row.speciesInvasiveCount ?? 0
                    : row.metricValue ?? 0;
            return [
                row.id,
                metricValue,
                row.name,
                <ViewButton
                    site={{ id: row.id, hasEdna: row.hasEdna, hasHabitatInsights: row.hasHabitatInsights }}
                    key={row.id}
                    locationForTracking='sidebar'
                />,
            ];
        });

        setDataTableState(prev => ({
            ...prev,
            data: tableRows,
        }));
    }, [rows, selectedMetric]);

    const sidebarClassNames = classNames(styles.sidebar, {
        [styles.sidebarOpened]: isSidebarOpen,
    });

    const sidebarToggleClassNames = classNames(styles.sidebarToggle, {
        [styles.sidebarToggleSampleView]: isSidebarOpen,
    });

    // Available metrics should come from portfolio filters
    const { currentData: portfolioMapFilters } = usePortfolioMapFiltersQuery({
        customerId: currentCustomerId,
    });

    const metricOptions: SelectOption[] = useMemo(() => {
        const available = portfolioMapFilters?.availableMetrics || [];

        const base = available.map(metric => ({
            value: metric,
            label: habitatInsightsMetricTypeLabel[metric as HabitatInsightsMetricType] || metric,
        }));

        // USe "Combined vegetation cover" as label for LAND_COVER
        const landCoverMetric = base.find(metric => metric.value === HabitatInsightsMetricType.LAND_COVER);
        if (landCoverMetric) {
            landCoverMetric.label = 'Combined vegetation cover';
        }

        // Append species count metrics
        const extra: SelectOption[] = [
            { value: 'POSSIBLE_IUCN_RED_LIST_SPECIES', label: 'Predicted IUCN Red List species' },
            { value: 'POSSIBLE_INVASIVE_SPECIES', label: 'Predicted invasive species' },
        ];

        return base.length === 0 ? [{ value: 'NA', label: 'NA' }] : [...base, ...extra];
    }, [portfolioMapFilters?.availableMetrics]);

    // Set default metric to the first available metric
    React.useEffect(() => {
        if (selectedMetric === 'NA' && portfolioMapFilters?.availableMetrics) {
            dispatch(setSelectedMetric(portfolioMapFilters.availableMetrics[0]));
        }
    }, [portfolioMapFilters?.availableMetrics]);

    const handleStateChange = (newState: Partial<DataTableState>) => {
        setDataTableState(newState);
        // In client-side mode DataTable owns pagination/sort — don't update API params.
        if (!isServerSideSort) return;

        if (newState.currentPageNumber) {
            goToPage(newState.currentPageNumber);
        }
        if (newState.sortedColumn && !isSpeciesMetric) {
            const { columnId, sortedDirection } = newState.sortedColumn;
            const backendField = getBackendSortField(columnId, selectedMetric);
            if (backendField) {
                const newOrder = toApiSortOrder(sortedDirection);
                if (backendField !== sortState.field || newOrder !== sortState.order) {
                    setSortOverride({ field: backendField, order: newOrder });
                    goToPage(1);
                }
            }
        }
    };

    const handleMetricChange = (value: string) => {
        dispatch(setSelectedMetric(value));
        trackSidebarIndicatorSelect(value);
    };

    const handleRowClick = (rowData: TableRowData) => {
        const rowSiteId = rowData[0] as string;
        const siteId = selectedSiteId === rowSiteId ? null : rowSiteId; // Deselect if already selected

        // Track site click from sidebar
        if (siteId) {
            trackSiteClick(siteId, 'sidebar');
        }

        dispatch(setSelectedSite(siteId));
        setDataTableState(prev => ({
            ...prev,
            selectedRowIds: siteId ? new Set([siteId]) : new Set(),
        }));
    };

    // When selectedSiteId changes (from map click), scroll table to make it visible
    useEffect(() => {
        if (selectedSiteId) {
            // Find the row element by data attribute
            const container = document.querySelector(`.${styles.sidebarContent}`);
            const rowEl = container?.querySelector(`[data-row-id="${selectedSiteId}"]`) as HTMLElement | null;
            if (container && rowEl) {
                const containerRect = (container as HTMLElement).getBoundingClientRect();
                const rowRect = rowEl.getBoundingClientRect();
                const isVisible = rowRect.top >= containerRect.top && rowRect.bottom <= containerRect.bottom;
                if (!isVisible) {
                    rowEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                }
            }
            setDataTableState(prev => ({
                ...prev,
                selectedRowIds: new Set([selectedSiteId]),
            }));
        } else {
            setDataTableState(prev => ({
                ...prev,
                selectedRowIds: new Set(),
            }));
        }
    }, [selectedSiteId]);

    if (!isSiteMetricsFetching && rows.length === 0 && totalCount === 0) {
        return null;
    }

    return (
        <div className={sidebarClassNames}>
            <div className={sidebarToggleClassNames} onClick={() => setSidebarOpen(!isSidebarOpen)}>
                <span className={styles.sidebarTitleBackIcon}>{isSidebarOpen ? <CloseIcon /> : <ArrowBackIcon />}</span>
            </div>
            <Show when={isSidebarOpen}>
                <Show
                    when={isSiteMetricsFetching}
                    fallback={
                        <>
                            <div className={styles.sidebarTitle}>Sites</div>
                            <div className={styles.sidebarContent}>
                                <div className='flex flex-row gap-2 items-center'>
                                    <div className='mb-4'>
                                        <InputLabel className={styles.metricLabel}>Indicator</InputLabel>
                                        <div className='flex flex-row gap items-center'>
                                            <Select
                                                options={metricOptions}
                                                selectedValue={selectedMetric}
                                                onChange={handleMetricChange}
                                                width='220px'
                                                testId='metrics-select'
                                            />
                                            {habitatInsightsMetricExplainerSlugs[selectedMetric] && (
                                                <QuestionButtonHelp
                                                    type='api'
                                                    slug={habitatInsightsMetricExplainerSlugs[selectedMetric].defaultSlug}
                                                    detailedExplainerSlug={
                                                        habitatInsightsMetricExplainerSlugs[selectedMetric].explainerSlug
                                                    }
                                                    onDetailedHelpClick={slug => trackDetailedExplainerOpen(slug, 'map')}
                                                    onTooltipHover={() =>
                                                        trackSidebarIndicatorHelpHover(
                                                            habitatInsightsMetricExplainerSlugs[selectedMetric].explainerSlug
                                                        )
                                                    }
                                                />
                                            )}
                                        </div>
                                    </div>

                                    <div className='text-sm text-grey-80 ml-2'>
                                        Displaying <br />
                                        {rows.length} of {totalCount} sites
                                    </div>
                                </div>

                                <DataTable
                                    columns={columns}
                                    state={dataTableState}
                                    emptyStateMessage={<span>No sites found.</span>}
                                    onRowSelect={handleRowClick}
                                    onStateChange={handleStateChange}
                                    allowSortClear={false}
                                    totalRowsForPagination={totalCount}
                                    isServerSideSort={isServerSideSort}
                                />
                                {isSpeciesModalOpen && speciesModalSiteId && (
                                    <SiteSpeciesRiskModal
                                        onClose={() => setSpeciesModalOpen(false)}
                                        siteId={speciesModalSiteId}
                                        initialTab={speciesModalTab}
                                    />
                                )}
                            </div>
                        </>
                    }
                >
                    <div className={styles.sidebarContent}>
                        <Loader />
                    </div>
                </Show>
            </Show>
        </div>
    );
};

export default PortfolioSiteExplorerSidebar;
